// components/PatientMedicalHistory.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Appointment {
  id: number;
  professional_id: number;
  service_id: number;
  appointment_date: string;
  appointment_time: string;
  duration: number;
  status: string;
  price: number;
  notes: string;
  professional_notes: string;
  service_name: string;
  service_description: string;
  service_category: string;
  professional_name: string;
  professional_especialidad: string;
  professional_type: string;
  created_at: string;
  updated_at: string;
}

interface MedicalRecord {
  id: number;
  appointment: Appointment;
  diagnosis: string;
  treatment: string;
  medications: string;
  follow_up_notes: string;
  created_at: string;
}

const PatientMedicalHistory: React.FC = () => {
  const navigate = useNavigate();
  
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'salud' | 'belleza'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'completed' | 'cancelled'>('all');

  useEffect(() => {
    loadMedicalHistory();
  }, []);

  const loadMedicalHistory = async () => {
    setIsLoading(true);
    setError('');

    try {
      const userData = localStorage.getItem('user');
      if (!userData) {
        navigate('/login');
        return;
      }

      const user = JSON.parse(userData);
      
      // Cargar citas del paciente
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/patients/${user.id}/appointments`);
      
      if (!response.ok) {
        throw new Error('Error al cargar el historial médico');
      }

      const data = await response.json();
      
      // Filtrar solo citas completadas y pasadas para el historial médico
      const pastAppointments = data.appointments.filter((apt: Appointment) => {
        const appointmentDate = new Date(apt.appointment_date);
        const today = new Date();
        return appointmentDate < today;
      });

      setAppointments(pastAppointments);

    } catch (error) {
      console.error('Error cargando historial:', error);
      setError('Error al cargar el historial médico');
    } finally {
      setIsLoading(false);
    }
  };

  const getFilteredAppointments = () => {
    return appointments.filter(apt => {
      const matchesType = filterType === 'all' || apt.professional_type === filterType;
      const matchesStatus = filterStatus === 'all' || apt.status === filterStatus;
      return matchesType && matchesStatus;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800',
      'no_show': 'bg-orange-100 text-orange-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'confirmed': 'bg-blue-100 text-blue-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status: string) => {
    const texts = {
      'completed': 'Completada',
      'cancelled': 'Cancelada',
      'no_show': 'No asistió',
      'pending': 'Pendiente',
      'confirmed': 'Confirmada'
    };
    return texts[status as keyof typeof texts] || status;
  };

  const filteredAppointments = getFilteredAppointments();
  const completedAppointments = appointments.filter(apt => apt.status === 'completed');
  const totalVisits = appointments.length;
  const uniqueProfessionals = new Set(appointments.map(apt => apt.professional_id)).size;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando historial médico...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-3xl font-bold text-gray-900">Mi Historial Médico</h1>
              <p className="text-gray-600 mt-1">Consulta todas tus citas y tratamientos anteriores</p>
            </div>
            <button
              onClick={() => navigate('/dashboard/paciente')}
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Volver al Dashboard
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
            <button
              onClick={loadMedicalHistory}
              className="mt-2 text-red-600 hover:text-red-800 font-medium"
            >
              Intentar nuevamente
            </button>
          </div>
        )}

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de visitas</p>
                <p className="text-2xl font-bold text-gray-900">{totalVisits}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-full">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completadas</p>
                <p className="text-2xl font-bold text-gray-900">{completedAppointments.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-full">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Profesionales</p>
                <p className="text-2xl font-bold text-gray-900">{uniqueProfessionals}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-full">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Gasto total</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${completedAppointments.reduce((sum, apt) => sum + (apt.price || 0), 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de profesional
              </label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as 'all' | 'salud' | 'belleza')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Todos los tipos</option>
                <option value="salud">Salud</option>
                <option value="belleza">Belleza</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estado
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | 'completed' | 'cancelled')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Todos los estados</option>
                <option value="completed">Completadas</option>
                <option value="cancelled">Canceladas</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setFilterType('all');
                  setFilterStatus('all');
                }}
                className="w-full px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Limpiar filtros
              </button>
            </div>
          </div>
        </div>

        {/* Lista de citas */}
        <div className="bg-white rounded-xl shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Historial de citas ({filteredAppointments.length})
            </h2>
          </div>
          
          {filteredAppointments.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {filteredAppointments.map((appointment) => (
                <div
                  key={appointment.id}
                  className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => setSelectedAppointment(appointment)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {appointment.service_name}
                          </h3>
                          <p className="text-blue-600 font-medium">
                            {appointment.professional_name} - {appointment.professional_especialidad}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(appointment.status)}`}>
                          {getStatusText(appointment.status)}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-2">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {formatDate(appointment.appointment_date)}
                        </div>
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {formatTime(appointment.appointment_time)} ({appointment.duration} min)
                        </div>
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                          ${appointment.price}
                        </div>
                      </div>
                      
                      {appointment.service_description && (
                        <p className="text-gray-600 text-sm">
                          {appointment.service_description}
                        </p>
                      )}
                      
                      {(appointment.notes || appointment.professional_notes) && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          {appointment.notes && (
                            <div className="mb-2">
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Mis notas
                              </p>
                              <p className="text-sm text-gray-700">{appointment.notes}</p>
                            </div>
                          )}
                          {appointment.professional_notes && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                Notas del profesional
                              </p>
                              <p className="text-sm text-gray-700">{appointment.professional_notes}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4 sm:mt-0 sm:ml-6 flex items-center">
                      <button className="text-blue-600 hover:text-blue-800 font-medium">
                        Ver detalles
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {appointments.length === 0 ? 'No tienes historial médico' : 'No hay citas que coincidan con los filtros'}
              </h3>
              <p className="text-gray-500 mb-6">
                {appointments.length === 0 
                  ? 'Cuando tengas citas completadas, aparecerán aquí.'
                  : 'Intenta cambiar los filtros para ver más resultados.'
                }
              </p>
              <button
                onClick={() => navigate('/search')}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Agendar mi primera cita
              </button>
            </div>
          )}
        </div>

        {/* Modal de detalle de cita */}
        {selectedAppointment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900">
                    Detalle de la cita
                  </h3>
                  <button
                    onClick={() => setSelectedAppointment(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">{selectedAppointment.service_name}</h4>
                    <p className="text-gray-600">{selectedAppointment.service_description}</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-gray-900 mb-1">Profesional</h5>
                      <p className="text-gray-600">{selectedAppointment.professional_name}</p>
                      <p className="text-sm text-gray-500">{selectedAppointment.professional_especialidad}</p>
                    </div>
                    
                    <div>
                      <h5 className="font-medium text-gray-900 mb-1">Fecha y hora</h5>
                      <p className="text-gray-600">
                        {formatDate(selectedAppointment.appointment_date)} a las {formatTime(selectedAppointment.appointment_time)}
                      </p>
                      <p className="text-sm text-gray-500">Duración: {selectedAppointment.duration} minutos</p>
                    </div>
                    
                    <div>
                      <h5 className="font-medium text-gray-900 mb-1">Estado</h5>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedAppointment.status)}`}>
                        {getStatusText(selectedAppointment.status)}
                      </span>
                    </div>
                    
                    <div>
                      <h5 className="font-medium text-gray-900 mb-1">Precio</h5>
                      <p className="text-gray-600">${selectedAppointment.price}</p>
                    </div>
                  </div>
                  
                  {selectedAppointment.notes && (
                    <div>
                      <h5 className="font-medium text-gray-900 mb-1">Mis notas</h5>
                      <p className="text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedAppointment.notes}</p>
                    </div>
                  )}
                  
                  {selectedAppointment.professional_notes && (
                    <div>
                      <h5 className="font-medium text-gray-900 mb-1">Notas del profesional</h5>
                      <p className="text-gray-600 bg-blue-50 p-3 rounded-lg">{selectedAppointment.professional_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientMedicalHistory;